import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/api";
import { BellRing, Send, Clock, Users, Check, Trash2, Radio, FlaskConical, Search } from "lucide-react";
import { fmtTime, timeAgo } from "@/lib/constants";
import { toast } from "sonner";

export default function PageMe() {
  const [devices, setDevices] = useState([]);
  const [messages, setMessages] = useState([]);
  const [allTargets, setAllTargets] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [mode, setMode] = useState("bericht"); // bericht | test
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [when, setWhen] = useState("now"); // now | plan
  const [q, setQ] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, m] = await Promise.all([api.get("/pager/devices"), api.get("/pager/messages")]);
      setDevices((d.data || []).filter((x) => x.status === "approved"));
      setMessages(m.data || []);
    } catch (err) { /* ignore */ }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const send = async () => {
    if (!allTargets && selected.size === 0) { toast.error("Selecteer minstens één pager"); return; }
    if (mode === "bericht" && !text.trim()) { toast.error("Vul een bericht in"); return; }
    let scheduled_at = null;
    if (when === "plan") {
      if (!scheduledAt) { toast.error("Kies een datum en tijd"); return; }
      const dt = new Date(scheduledAt);
      if (isNaN(dt.getTime())) { toast.error("Ongeldige tijd"); return; }
      scheduled_at = dt.toISOString();
    }
    setSending(true);
    try {
      const content = mode === "test"
        ? { title: "Testbericht", text: "Dit is een testbericht", kind: "test" }
        : { title: title.trim() || null, text: text.trim(), kind: "real" };
      await api.post("/pager/messages", { pager_ids: allTargets ? [] : Array.from(selected), ...content, scheduled_at });
      toast.success(scheduled_at ? "Pager-oproep gepland" : (mode === "test" ? "Testbericht verstuurd" : "Bericht verstuurd"));
      setText(""); setTitle(""); setWhen("now"); setScheduledAt("");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const cancel = async (id) => { try { await api.delete(`/pager/messages/${id}`); toast.success("Geplande oproep geannuleerd"); load(); } catch (e) {} };

  const pending = messages.filter((m) => m.status === "pending");
  const sent = messages.filter((m) => m.status !== "pending").slice(0, 10);

  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="pageme">
      <div>
        <h1 className="flex items-center gap-2 font-display text-2xl font-bold text-slate-900"><Radio className="h-6 w-6 text-blue-600" /> PageMe — pager-oproep</h1>
        <p className="mt-1 text-sm text-slate-500">Stuur een bericht of een standaard testbericht naar één of meer pagers (personen of huispagers) — nu of op een gepland moment.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-900"><Users style={{ width: 16, height: 16 }} /> Ontvangers</div>
            <label className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
              <input data-testid="pageme-all" type="checkbox" checked={allTargets} onChange={(e) => setAllTargets(e.target.checked)} className="h-4 w-4" />
              Alle gekoppelde pagers ({devices.length})
            </label>
            {!allTargets && (
              <div className="space-y-2">
                <div className="relative">
                  <Search style={{ width: 15, height: 15 }} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input data-testid="pageme-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op naam of locatie…"
                    className="w-full rounded-xl border border-slate-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-500" />
                </div>
                {(() => {
                  const ql = q.trim().toLowerCase();
                  const filtered = devices.filter((d) => !ql || (d.label || "").toLowerCase().includes(ql) || (d.location_name || "").toLowerCase().includes(ql));
                  if (devices.length === 0) return <div className="text-sm text-slate-400">Geen gekoppelde pagers.</div>;
                  if (filtered.length === 0) return <div className="text-sm text-slate-400">Geen pagers gevonden voor "{q}".</div>;
                  return (
                    <div className="grid gap-2 sm:grid-cols-2" data-testid="pageme-device-list">
                      {filtered.map((d) => (
                        <label key={d.id} data-testid={`pageme-device-${d.id}`} className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm ${selected.has(d.id) ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-slate-50"}`}>
                          <input type="checkbox" checked={selected.has(d.id)} onChange={() => toggle(d.id)} className="h-4 w-4" />
                          <span className="min-w-0"><span className="font-semibold text-slate-900">{d.label}</span>{d.location_name && <span className="ml-1 text-xs text-slate-500">· {d.location_name}</span>}</span>
                        </label>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 grid gap-2 sm:grid-cols-2">
              <button data-testid="pageme-mode-bericht" onClick={() => setMode("bericht")} className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold ${mode === "bericht" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700"}`}><BellRing className="h-4 w-4" /> Bericht</button>
              <button data-testid="pageme-mode-test" onClick={() => setMode("test")} className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold ${mode === "test" ? "bg-amber-500 text-slate-900" : "bg-slate-100 text-slate-700"}`}><FlaskConical className="h-4 w-4" /> Testbericht</button>
            </div>
            {mode === "bericht" ? (
              <>
                <input data-testid="pageme-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titel (optioneel), bijv. Opkomst kazerne"
                  className="mb-3 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
                <textarea data-testid="pageme-text" value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Berichttekst…"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
              </>
            ) : (
              <p className="text-sm text-amber-700" data-testid="pageme-testinfo">Er wordt een standaard testbericht gestuurd: <span className="font-semibold">"Dit is een testbericht"</span>. Je hoeft niets in te tikken.</p>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-900"><Clock style={{ width: 16, height: 16 }} /> Verzendmoment</div>
            <div className="grid gap-2 sm:grid-cols-2">
              <button data-testid="pageme-when-now" onClick={() => setWhen("now")} className={`rounded-xl px-3 py-2.5 text-sm font-semibold ${when === "now" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700"}`}>Nu versturen</button>
              <button data-testid="pageme-when-plan" onClick={() => setWhen("plan")} className={`rounded-xl px-3 py-2.5 text-sm font-semibold ${when === "plan" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700"}`}>Plannen</button>
            </div>
            {when === "plan" && (
              <input data-testid="pageme-scheduled" type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)}
                className="mt-3 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
            )}
            <button data-testid="pageme-send" onClick={send} disabled={sending}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-3 text-sm font-bold text-white hover:bg-slate-700 disabled:opacity-60">
              <Send className="h-4 w-4" /> {when === "plan" ? "Plan pager-oproep" : "Verstuur nu"}
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {pending.length > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 shadow-sm" data-testid="pageme-pending">
              <div className="mb-3 flex items-center gap-2 text-sm font-bold text-amber-800"><Clock style={{ width: 16, height: 16 }} /> Gepland ({pending.length})</div>
              <div className="space-y-2">
                {pending.map((m) => (
                  <div key={m.id} data-testid={`pageme-pending-${m.id}`} className="rounded-xl bg-white p-3 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-slate-900">{m.title || m.text.slice(0, 30)}</span>
                      <button data-testid={`pageme-cancel-${m.id}`} onClick={() => cancel(m.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:underline"><Trash2 className="h-3.5 w-3.5" /> Annuleer</button>
                    </div>
                    <div className="mt-1 text-xs text-slate-500">{m.kind === "test" ? "Test" : "Echt"} · {m.pager_ids?.length ? `${m.pager_ids.length} pager(s)` : "alle pagers"} · om {fmtTime(m.scheduled_at)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="pageme-history">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-900"><Check style={{ width: 16, height: 16 }} /> Recent verzonden</div>
            {sent.length === 0 ? <div className="text-sm text-slate-400">Nog niets verzonden.</div> : (
              <div className="space-y-2">
                {sent.map((m) => (
                  <div key={m.id} className="rounded-xl bg-slate-50 p-3 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-slate-900">{m.title || m.text.slice(0, 30)}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.kind === "test" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>{m.kind === "test" ? "Test" : "Echt"}</span>
                    </div>
                    <div className="mt-1 text-xs text-slate-500">{m.pager_ids?.length ? `${m.pager_ids.length} pager(s)` : "alle pagers"} · {timeAgo(m.sent_at || m.created_at)} · door {m.created_by}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
